import { useMemo, useState } from "react";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../hooks/useNotification";
import {
  useApproveSaleInvoiceMutation,
  useDeleteSaleInvoiceMutation,
  useMarkSaleInvoiceAsPaidMutation,
  useVoidSaleInvoiceMutation,
  useWriteOffSaleInvoiceMutation,
} from "../../../store/apiSlice/invoiceApiSlice";
import { componentWithCondition } from "../../newLayout/page/components/hoc/componentWithConditionCheck";
import { useSubmitSaleInvoice } from "../detail/useSubmitSaleInvoice";
import {
  saleInvoiceApproveFulfillmentVisibility,
  saleInvoiceExtraActionAbility,
} from "../hooks/saleInvoiceActionVisibility";
import { useProcessSaleInvoiceFulfillment } from "../hooks/useProcessSaleInvoiceFulfillment";
import { useSaleStockDepletionTrigger } from "../hooks/useSaleStockDepletionTrigger";
import { RecordCustomerPaymentModal } from "../components/RecordCustomerPaymentModal";
import { saleInvoiceOpenAmountDue } from "../hooks/saleInvoiceActionVisibility";
import { ButtonSpinner } from "../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

export const SaleInvoiceAction = ({
  selectedOrderId,
  selectedOrderFromListview,
  setSelectedOrder,
}) => {
  const [approveSaleInvoice, { isLoading: isApproving }] = useApproveSaleInvoiceMutation();
  const [markSaleInvoiceAsPaid, { isLoading: isMarkingPaid }] = useMarkSaleInvoiceAsPaidMutation();
  const [voidSaleInvoice, { isLoading: isVoiding }] = useVoidSaleInvoiceMutation();
  const [writeOffSaleInvoice, { isLoading: isWritingOff }] = useWriteOffSaleInvoiceMutation();
  const [deleteSaleInvoice, { isLoading: isDeleting }] = useDeleteSaleInvoiceMutation();
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const notify = useNotification();
  const { isInvoiceApprovalDepletion } = useSaleStockDepletionTrigger();
  const {
    closeConfirm,
    confirm,
    invoiceCount,
    isConfirmOpen,
    isLoading: isFulfilling,
    lineCount,
    openConfirm,
  } = useProcessSaleInvoiceFulfillment({
    onComplete: () => setSelectedOrder([]),
  });

  const { status, id } = selectedOrderFromListview[0] || {};
  const { submit, isSubmitting } = useSubmitSaleInvoice({
    onComplete: () => setSelectedOrder([]),
  });

  const { canApprove, canFulfill } = useMemo(
    () => saleInvoiceApproveFulfillmentVisibility(selectedOrderFromListview),
    [selectedOrderFromListview],
  );
  const managedInXero = Boolean(
    selectedOrderFromListview?.length === 1 &&
      selectedOrderFromListview[0]?.managed_in_xero,
  );
  const selectedInvoice = selectedOrderFromListview?.[0];
  const extraAbility = useMemo(
    () =>
      saleInvoiceExtraActionAbility(selectedOrderId?.length || 0, status?.id, {
        managedInXero,
        amountDue: saleInvoiceOpenAmountDue(selectedInvoice),
      }),
    [selectedOrderId?.length, status?.id, managedInXero, selectedInvoice],
  );

  const handleAction = async (mutationFn, successMessage, errorMessage, invoiceId = id) => {
    try {
      const res = await mutationFn({ id: invoiceId }).unwrap();
      notify({
        type: "success",
        message: res?.message || successMessage,
      });
      return true;
    } catch (error) {
      const err = error?.data;
      if (err?.detail) {
        notify({ type: "error", message: err.detail });
      } else {
        notify({ type: "error", message: errorMessage });
      }
      return false;
    }
  };

  const runApprove = async () => {
    const ids = selectedOrderId?.length ? selectedOrderId : id ? [id] : [];
    for (const invoiceId of ids) {
      const ok = await handleAction(
        approveSaleInvoice,
        "Invoice approved successfully.",
        "Failed to approve invoice.",
        invoiceId,
      );
      if (!ok) return;
    }
    setSelectedOrder([]);
  };

  const handleApprove = () => {
    if (!canApprove || isApproving) return;
    if (isInvoiceApprovalDepletion) {
      setIsApproveConfirmOpen(true);
      return;
    }
    runApprove();
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "record-payment",
        label: "Record payment",
        disabled: !extraAbility.canRecordPayment,
        title: extraAbility.canRecordPayment
          ? ""
          : "Select a single authorised unpaid invoice to record payment.",
        onClick: () => setPaymentOpen(true),
      },
      {
        key: "mark-paid",
        label: "Mark as Paid",
        disabled: !extraAbility.canMarkPaid || isMarkingPaid,
        title: extraAbility.canMarkPaid
          ? ""
          : "Select a single authorised invoice to mark as paid.",
        isLoading: isMarkingPaid,
        onClick: async () => {
          if (
            await handleAction(
              markSaleInvoiceAsPaid,
              "Invoice marked as paid successfully.",
              "Failed to mark invoice as paid.",
            )
          ) {
            setSelectedOrder([]);
          }
        },
      },
      {
        key: "void",
        label: "Void",
        disabled: !extraAbility.canVoid || isVoiding,
        title: extraAbility.canVoid ? "" : "Select a single unpaid invoice to void.",
        isLoading: isVoiding,
        isDanger: true,
        onClick: async () => {
          if (
            await handleAction(
              voidSaleInvoice,
              "Invoice voided successfully.",
              "Failed to void invoice.",
            )
          ) {
            setSelectedOrder([]);
          }
        },
      },
      {
        key: "write-off",
        label: "Write Off",
        disabled: !extraAbility.canWriteOff || isWritingOff,
        title: extraAbility.canWriteOff
          ? ""
          : "Select a single overdue/sent invoice to write off.",
        isLoading: isWritingOff,
        onClick: async () => {
          if (
            await handleAction(
              writeOffSaleInvoice,
              "Invoice written off successfully.",
              "Failed to write off invoice.",
            )
          ) {
            setSelectedOrder([]);
          }
        },
      },
      {
        key: "delete",
        label: "Delete",
        disabled: !extraAbility.canDelete || isDeleting,
        title: extraAbility.canDelete ? "" : "Select a single draft invoice to delete.",
        isLoading: isDeleting,
        isDanger: true,
        onClick: async () => {
          if (
            await handleAction(
              deleteSaleInvoice,
              "Invoice deleted successfully.",
              "Failed to delete invoice.",
            )
          ) {
            setSelectedOrder([]);
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraAbility, isMarkingPaid, isVoiding, isWritingOff, isDeleting, id],
  );

  return (
    <>
      <li className={`tableButton ${extraAbility.canSubmit ? "activeState" : "disableState"}`}>
        <button
          type="button"
          disabled={!extraAbility.canSubmit || isSubmitting}
          title={extraAbility.canSubmit ? "" : "Select a single draft invoice to submit."}
          onClick={() => {
            if (!id) return;
            submit(id);
          }}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isSubmitting && <ButtonSpinner aria-hidden="true" />}
          {isSubmitting ? "Submitting…" : "Submit Invoice"}
        </button>
      </li>
      <li className={`tableButton ${canApprove ? "activeState" : "disableState"}`}>
        <button
          type="button"
          disabled={!canApprove || isApproving}
          title={canApprove ? "" : "Select at least one invoice in 'Awaiting Approval' status."}
          onClick={handleApprove}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isApproving && <ButtonSpinner aria-hidden="true" />}
          {isApproving ? "Approving…" : "Approve"}
        </button>
      </li>
      <li className={`tableButton ${canFulfill ? "activeState" : "disableState"}`}>
        <button
          type="button"
          disabled={!canFulfill || isFulfilling}
          title={canFulfill ? "" : "Select invoices with open fulfillment issues to retry."}
          onClick={() => openConfirm({ invoiceIds: selectedOrderId })}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isFulfilling && <ButtonSpinner aria-hidden="true" />}
          {isFulfilling ? "Processing…" : "Process Fulfillment"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />
      {isConfirmOpen && (
        <ConfirmationModal
          titleOfModal="Process Fulfillment"
          btnName="Process Fulfillment"
          closeModal={closeConfirm}
          handleModalWork={confirm}
          isLoading={isFulfilling}
          description={
            <>
              Process fulfillment for {invoiceCount} invoice(s)
              {lineCount ? ` / ${lineCount} line(s)` : ""}. This will create or continue pick, pack,
              delivery, and shipment, then take stock.
            </>
          }
        />
      )}
      {isApproveConfirmOpen && (
        <ConfirmationModal
          titleOfModal="Approve"
          btnName="Approve"
          closeModal={() => setIsApproveConfirmOpen(false)}
          handleModalWork={() => {
            setIsApproveConfirmOpen(false);
            runApprove();
          }}
          isLoading={isApproving}
          description={
            <>
              Approving this invoice will despatch stock from the sales-order warehouse. If stock is
              short, the invoice will still be approved and an issue will be recorded.
            </>
          }
        />
      )}
      <RecordCustomerPaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        invoice={selectedInvoice}
        onRecorded={() => setSelectedOrder([])}
      />
    </>
  );
};

export const SaleInvoiceActionWithCheck = componentWithCondition(SaleInvoiceAction);
